// Single source of truth for the /inventory_adjustment_form reason codes.
//
// Mirrors EnumLi.AdjustmentReason on the backend — the ids MUST stay in sync,
// they are stored on inventory_adjustments.Reason and they drive the GL account
// each adjustment posts to (see JournalRulesProvider.InventoryAdjustmentRules).
// Adding a reason here without adding it to the enum silently posts to the
// coarse Inventory Adjustment Gain / Loss fallback account.

// Increase reasons (stock IN) — enum values 1-10.
export const INCREASE_REASONS = [
  { id: 1, labelKey: "reasonOpeningBalance" },
  { id: 2, labelKey: "reasonDirectPurchase" },
  { id: 3, labelKey: "reasonCustomerReturn" },
  { id: 4, labelKey: "reasonFound" },
  { id: 5, labelKey: "reasonProduction" },
  { id: 6, labelKey: "reasonTransferIn" },
  { id: 7, labelKey: "reasonDonationReceived" },
  { id: 8, labelKey: "reasonRecount" },
  { id: 9, labelKey: "reasonCorrection" },
  { id: 10, labelKey: "reasonOther" },
];

// Decrease reasons (stock OUT) — enum values 11-21.
//
// Damaged and Wastage are deliberately separate: damage is an abnormal one-off
// incident written off to operating expenses, wastage is expected process loss
// absorbed into COGS. They post to different accounts.
export const DECREASE_REASONS = [
  { id: 11, labelKey: "reasonDamaged" },
  { id: 21, labelKey: "reasonWastage" },
  { id: 14, labelKey: "reasonExpired" },
  { id: 12, labelKey: "reasonLost" },
  { id: 13, labelKey: "reasonTheft" },
  { id: 15, labelKey: "reasonConsumed" },
  { id: 16, labelKey: "reasonTransferOut" },
  { id: 17, labelKey: "reasonDonationGiven" },
  { id: 18, labelKey: "reasonRecount" },
  { id: 19, labelKey: "reasonCorrection" },
  { id: 20, labelKey: "reasonOther" },
];

// AdjustmentType enum values.
export const ADJUSTMENT_TYPE = { increase: 1, decrease: 2 };

// AdjustmentSource enum values — which door the document came through.
export const ADJUSTMENT_SOURCE = { backOffice: 1, counterWastage: 2 };

// The only reasons a cashier may pick on /stock_wastage. Mirrors EnumLi.CounterLossReasons
// on the backend, which REJECTS anything outside this list — offering a reason here
// that the server does not accept would just produce a 400 at submit time.
//
// Theft, shrinkage, internal consumption, donations, transfers and the catch-all
// Correction / Other are deliberately absent: they carry investigation or approval
// weight and belong on the back-office form.
export const COUNTER_LOSS_REASON_IDS = [21, 11, 14, 12];

// Wastage — what a counter entry is by far most often, so the form opens on it
// rather than making the cashier pick the common case every time. Listed first in
// COUNTER_LOSS_REASON_IDS for the same reason; kept as its own named export so the
// default cannot drift if that order is ever changed.
export const COUNTER_DEFAULT_REASON_ID = 21;

// id -> i18n key, for rendering a stored reason back on the history list.
export const ADJUSTMENT_REASON_LABEL_KEYS = [
  ...INCREASE_REASONS,
  ...DECREASE_REASONS,
].reduce((acc, r) => ({ ...acc, [r.id]: r.labelKey }), {});

/**
 * Reason options for a given adjustment type, translated and ready for a select.
 *
 * @param {number} adjustmentType ADJUSTMENT_TYPE.increase | ADJUSTMENT_TYPE.decrease
 * @param {(key: string) => string} t i18n translator
 * @returns {{id: number, label: string}[]} empty when no type is picked yet
 */
export const getAdjustmentReasonOptions = (adjustmentType, t) => {
  const source =
    adjustmentType === ADJUSTMENT_TYPE.increase
      ? INCREASE_REASONS
      : adjustmentType === ADJUSTMENT_TYPE.decrease
        ? DECREASE_REASONS
        : [];

  return source.map(({ id, labelKey }) => ({ id, label: t(labelKey) }));
};

/**
 * Reason options a cashier may record from the counter, in the curated order.
 *
 * @param {(key: string) => string} t i18n translator
 * @returns {{id: number, label: string}[]}
 */
export const getCounterLossReasonOptions = (t) =>
  COUNTER_LOSS_REASON_IDS.map((id) => {
    const reason = DECREASE_REASONS.find((r) => r.id === id);
    return { id, label: t(reason.labelKey) };
  });

/**
 * Translate a stored reason id back to its label.
 *
 * @param {number} reasonId value held on inventory_adjustments.Reason
 * @param {(key: string) => string} t i18n translator
 * @returns {string} empty string for an unknown id
 */
export const getAdjustmentReasonLabel = (reasonId, t) => {
  const key = ADJUSTMENT_REASON_LABEL_KEYS[reasonId];
  return key ? t(key) : "";
};
